import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MapPin, Store, Plus, Scissors, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useShopsByOwner, useCreateShop, useCities } from '../../../api';
import { formatShopAddress } from '../../../utils/shopAddress';
import styles from './services.module.css';

const VendorShops = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [showCreateShopModal, setShowCreateShopModal] = useState(false);

  const [shopFormData, setShopFormData] = useState({
    name: '',
    description: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'India',
      coordinates: {
        latitude: '',
        longitude: '',
      },
    },
    contact: {
      phone: '',
      email: '',
      website: '',
    },
    timings: {
      monday: { open: '09:00', close: '21:00', isOpen: true },
      tuesday: { open: '09:00', close: '21:00', isOpen: true },
      wednesday: { open: '09:00', close: '21:00', isOpen: true },
      thursday: { open: '09:00', close: '21:00', isOpen: true },
      friday: { open: '09:00', close: '21:00', isOpen: true },
      saturday: { open: '09:00', close: '22:00', isOpen: true },
      sunday: { open: '10:00', close: '20:00', isOpen: true },
    },
    services: [],
    amenities: [],
    images: [],
  });

  const { data: cities = [] } = useCities();
  const userId = user?._id;

  const { data: allShops = [], refetch: refetchShops } = useShopsByOwner(userId);

  const shops = allShops.filter((shop) => {
    return user?.role === 'vendor' || user?.role === 'VENDER' ? true : shop.verified;
  });

  const { mutateAsync: createShop, isPending: isCreatingShop } = useCreateShop();

  const handleCreateShop = async (e) => {
    e.preventDefault();
    if (!shopFormData.name.trim()) {
      toast.error('Please provide a barbershop name.');
      return;
    }
    try {
      const shopData = {
        ...shopFormData,
        owner: userId,
        verified: false,
        rating: 0,
        totalReviews: 0,
        status: 'PENDING',
      };

      await createShop(shopData);
      toast.success('Barbershop registered successfully! Under verification review.');
      setShowCreateShopModal(false);
      setShopFormData({
        name: '',
        description: '',
        address: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'India',
          coordinates: { latitude: '', longitude: '' },
        },
        contact: { phone: '', email: '', website: '' },
        timings: {
          monday: { open: '09:00', close: '21:00', isOpen: true },
          tuesday: { open: '09:00', close: '21:00', isOpen: true },
          wednesday: { open: '09:00', close: '21:00', isOpen: true },
          thursday: { open: '09:00', close: '21:00', isOpen: true },
          friday: { open: '09:00', close: '21:00', isOpen: true },
          saturday: { open: '09:00', close: '22:00', isOpen: true },
          sunday: { open: '10:00', close: '20:00', isOpen: true },
        },
        services: [],
        amenities: [],
        images: [],
      });
      refetchShops();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create shop.');
    }
  };

  return (
    <div className={styles.container}>
      {/* Page Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Shops &amp; Services</h1>
          <p className={styles.subtitle}>
            Manage your barbershop locations, services catalogue, pricing tiers, and operating
            hours.
          </p>
        </div>

        <button className={styles.btnAddShop} onClick={() => setShowCreateShopModal(true)}>
          <Plus size={14} />
          <span>Register New Shop</span>
        </button>
      </div>

      {/* Shops Grid */}
      {shops.length > 0 ? (
        <div className={styles.shopGrid}>
          {shops.map((shop) => {
            const formattedAddr = formatShopAddress(shop.address) || 'Address not specified';
            const coverImage = shop.images?.[0]?.url || shop.images?.[0];

            return (
              <div key={shop._id} className={styles.shopCard}>
                <div className={styles.shopImageWrapper}>
                  {coverImage ? (
                    <img src={coverImage} alt={shop.name} className={styles.shopImage} />
                  ) : (
                    <div className={styles.placeholderImage}>
                      <Store size={32} />
                      <span>{shop.name}</span>
                    </div>
                  )}
                </div>

                <div className={styles.shopBody}>
                  <div className={styles.shopHeaderRow}>
                    <h3 className={styles.shopName}>{shop.name}</h3>
                    <span
                      className={`${styles.shopBadge} ${
                        shop.verified ? styles.verified : styles.pending
                      }`}
                    >
                      {shop.verified ? 'Verified' : 'Pending'}
                    </span>
                  </div>

                  <div className={styles.shopAddressLine}>
                    <MapPin
                      size={13}
                      style={{ flexShrink: 0, marginTop: '2px', color: '#94a3b8' }}
                    />
                    <span>{formattedAddr}</span>
                  </div>

                  <p className={styles.shopDescription}>
                    {shop.description || 'Professional barbershop services on B4BARBER.'}
                  </p>

                  <div className={styles.shopActions}>
                    <span className={styles.shopServiceTag}>
                      {shop.products?.length || 0} Services
                    </span>

                    <button
                      className={styles.btnActionPrimary}
                      onClick={() => navigate(`/shops/${shop._id}`)}
                    >
                      <Scissors size={13} />
                      <span>Manage Catalog</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          className={styles.emptyState}
          style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0' }}
        >
          <Store size={40} style={{ color: '#94a3b8', marginBottom: '8px' }} />
          <h3
            style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' }}
          >
            No Barbershops Registered
          </h3>
          <p style={{ fontSize: '0.8rem', color: '#64748b', maxWidth: 360, margin: '0 0 1rem 0' }}>
            You have not registered any salon locations yet. Add your first barbershop to begin
            receiving customer appointments.
          </p>
          <button className={styles.btnAddShop} onClick={() => setShowCreateShopModal(true)}>
            <Plus size={14} />
            <span>Add Your First Shop</span>
          </button>
        </div>
      )}

      {/* Register New Shop Modal */}
      {showCreateShopModal && (
        <div className={styles.modalBackdrop} onClick={() => setShowCreateShopModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Register New Barbershop</h3>
              <button
                className={styles.modalCloseBtn}
                onClick={() => setShowCreateShopModal(false)}
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleCreateShop}>
              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Shop Name *</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    placeholder="e.g. Vintage Cuts & Grooming"
                    value={shopFormData.name}
                    onChange={(e) => setShopFormData({ ...shopFormData, name: e.target.value })}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Description</label>
                  <textarea
                    rows={2}
                    className={styles.formTextarea}
                    placeholder="Brief description of your salon atmosphere and expertise…"
                    value={shopFormData.description}
                    onChange={(e) =>
                      setShopFormData({ ...shopFormData, description: e.target.value })
                    }
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Street Address</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    placeholder="Shop No., Building, Street Name"
                    value={shopFormData.address.street}
                    onChange={(e) =>
                      setShopFormData({
                        ...shopFormData,
                        address: { ...shopFormData.address, street: e.target.value },
                      })
                    }
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>City</label>
                    <select
                      className={styles.formSelect}
                      value={shopFormData.address.city}
                      onChange={(e) =>
                        setShopFormData({
                          ...shopFormData,
                          address: { ...shopFormData.address, city: e.target.value },
                        })
                      }
                    >
                      <option value="">Select City</option>
                      {cities.map((c) => (
                        <option key={c._id} value={c.cityName}>
                          {c.cityName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Postal Code</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      placeholder="e.g. 560001"
                      value={shopFormData.address.postalCode}
                      onChange={(e) =>
                        setShopFormData({
                          ...shopFormData,
                          address: { ...shopFormData.address, postalCode: e.target.value },
                        })
                      }
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Contact Phone</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      placeholder="+91 9876543210"
                      value={shopFormData.contact.phone}
                      onChange={(e) =>
                        setShopFormData({
                          ...shopFormData,
                          contact: { ...shopFormData.contact, phone: e.target.value },
                        })
                      }
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Contact Email</label>
                    <input
                      type="email"
                      className={styles.formInput}
                      placeholder="shop@example.com"
                      value={shopFormData.contact.email}
                      onChange={(e) =>
                        setShopFormData({
                          ...shopFormData,
                          contact: { ...shopFormData.contact, email: e.target.value },
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={styles.btnCancel}
                  onClick={() => setShowCreateShopModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.btnSubmit} disabled={isCreatingShop}>
                  {isCreatingShop ? 'Registering…' : 'Register Barbershop'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorShops;
